import Sentry from '@/services/sentry'
import config from '@/config'
import store from '@/vuex/store'
import mixpanelUtils from '@/utils/mixpanel.utils'
import { isDesktopOfficeHost, readDocumentSetting, saveDocumentSetting } from '@/services/office/helpers'

export const INVITE_CODES_SETTING_KEY = 'ahaInviteEditCodes'

const attemptedPresentationIds = new Set()

// Not offered in the EU region, so no code is stored there either.
const isAutoJoinEnabled = () => config.region !== 'eu'
  && store.state.isEnableAddinPowerpoint
  && store.getters['pptAddin/canUseAutoJoinEditorViaPpt']

// Loaded on demand: this module is reached from the store (through featureToggles and insert-slides),
// which main.ts evaluates before Vue.use(VueResource), and the invite API calls Vue.resource on load.
const loadInviteApi = async () => (await import('@/api/presentation-invite')).default

const readStoredCodes = async () => {
  const stored = await readDocumentSetting(INVITE_CODES_SETTING_KEY)
  return stored && typeof stored === 'object' ? stored : {}
}

let storedCodesUpdate = Promise.resolve()

const updateStoredCodes = (changeCodes) => {
  const update = storedCodesUpdate.then(async () => {
    const changedCodes = changeCodes(await readStoredCodes())
    if (!changedCodes) return null
    return saveDocumentSetting(INVITE_CODES_SETTING_KEY, changedCodes)
  })
  storedCodesUpdate = update.catch(() => {})
  return update
}

const resolveWithin = (promise, timeoutMs) => {
  let timer
  promise.catch(() => {})
  const timedOut = new Promise((resolve) => { timer = setTimeout(resolve, timeoutMs, null) })
  return Promise.race([promise, timedOut]).finally(() => clearTimeout(timer))
}

// The PPT code has its own server-side attribute and is not switched off with the invite link,
// so its `active` flag is ignored. The server decides who may join (same team or email domain).
const applyPPTSharedCode = (codes, presentationId, token) => (
  codes[presentationId] === token ? null : { ...codes, [presentationId]: token }
)

const persistInviteCode = async (presentationId, tokenLookupTimeoutMs) => {
  try {
    if (!isAutoJoinEnabled() || !presentationId) return
    // The code is not rotated, so one already in the document needs no request.
    if ((await readStoredCodes())[presentationId]) return
    const tokenLookup = loadInviteApi().then((api) => api.getPPTSharedCode(presentationId))
    const tokenInfo = await (tokenLookupTimeoutMs ? resolveWithin(tokenLookup, tokenLookupTimeoutMs) : tokenLookup)
    if (!tokenInfo?.token) return
    await updateStoredCodes((codes) => applyPPTSharedCode(codes, presentationId, tokenInfo.token))
  } catch (error) {
    // A user without edit rights, or a flag that is off for the owner, gets a 4xx here; that is expected.
    if (!(error?.status >= 400 && error?.status < 500)) Sentry.captureException(error)
  }
}

/**
 * Stores the invite code of a presentation that was just ADDED into the PowerPoint deck.
 * `binding` is what was added: { presentationId, slideId } for one slide, { activePresentationId }
 * for a whole presentation. The code is keyed by presentation. Never rejects.
 */
export const persistInviteCodeForDocument = (binding) => persistInviteCode(
  binding?.presentationId || binding?.activePresentationId,
)

export const BEFORE_INSERT_TOKEN_LOOKUP_TIMEOUT_MS = 5000

/**
 * Await this BEFORE an insert that clones the host slide. On desktop a clone keeps the
 * document.settings it was exported with, so a code stored afterwards never reaches the new slides.
 * The token lookup is bounded so a slow network cannot hold the insert; nothing is written once it
 * has timed out. Does nothing on the web, where settings are shared live. Never rejects.
 */
export const persistInviteCodeBeforeInsert = async (presentationId) => {
  if (!isDesktopOfficeHost()) return
  await persistInviteCode(presentationId, BEFORE_INSERT_TOKEN_LOOKUP_TIMEOUT_MS)
}

/**
 * The web half of persistInviteCodeBeforeInsert: call it once the insert has finished. Does nothing
 * on desktop, where the code was already stored before the export. Never rejects.
 */
export const persistInviteCodeAfterInsert = async (presentationId) => {
  if (isDesktopOfficeHost()) return
  await persistInviteCode(presentationId)
}

export const shouldAttemptAutoJoin = (presentationId) => isAutoJoinEnabled()
  && !attemptedPresentationIds.has(String(presentationId))

// Resolves false rather than rejecting when the join cannot be made.
export const tryAutoJoinWithStoredInviteCode = async (presentationId) => {
  attemptedPresentationIds.add(String(presentationId))
  mixpanelUtils.track('ppt_auto_join_via_invite_token_attempt', { presentationId })
  const fallback = (reason) => {
    mixpanelUtils.track('ppt_auto_join_via_invite_token_fallback', { presentationId, reason })
    return false
  }
  try {
    const codes = await readStoredCodes()
    const token = codes[presentationId]
    if (!token) return fallback('no_stored_code')
    await (await loadInviteApi()).joinViaSharingPPTFile(presentationId, token)
  } catch (error) {
    return fallback(error?.status ? `join_failed_${error.status}` : 'join_failed')
  }
  mixpanelUtils.track('ppt_auto_join_via_invite_token_success', { presentationId })
  return true
}
